import React, { useContext, useEffect, useState, useRef } from 'react';
import { Table, Button, Modal, Input, Row, Col, Pagination, Switch, message, Tooltip, AutoComplete, Spin } from 'antd';
import { SearchOutlined, EyeOutlined, EditOutlined, BarcodeOutlined } from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';
import CDetalleArticuloEmpresaSucursal from './detalles/detalle-articulo-empresa.js';
import CFormularioArticuloSucursal from './detalles/formulario-articulo-sucursal.js';
import CCategoriaInicio from '../../publico/inicio/CCategoriaInicio.js';

const isBarcode = (value) => /^\d{8,13}$/.test(value?.trim() || '');

const CListaArticulosEmpresaSucursal = () => {
  const { user } = useContext(LoginContext);
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [visible, setVisible] = useState(false);
  const [formularioVisible, setFormularioVisible] = useState(false);
  const [articuloParaFormulario, setArticuloParaFormulario] = useState(null);
  /** Texto aplicado al listado (misma semántica que home: busqueda_texto) */
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 10 });

  const sucursalId = user?.usu_sucursal;

  useEffect(() => {
    fetchArticulos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busquedaTexto, categoriaFiltro]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const fetchAutocompleteSuggestions = async (value) => {
    if (!value || value.trim().length < 2 || !sucursalId) {
      setAutocompleteOptions([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setAutocompleteLoading(true);
      try {
        const queryParams = new URLSearchParams({
          q: value.trim(),
          sucursal: sucursalId
        });
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/articulo-sucursal/autocomplete?${queryParams.toString()}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.suggestions?.length) {
          setAutocompleteOptions([]);
          return;
        }
        const options = data.suggestions.map((item) => ({
          value: item.nombre,
          label: (
            <div>
              <div style={{ fontWeight: 500 }}>{item.nombre}</div>
              {item.marca && (
                <div style={{ fontSize: 12, color: '#888' }}>{item.marca}</div>
              )}
            </div>
          ),
          item
        }));
        setAutocompleteOptions(options);
      } catch (e) {
        console.error('Error al obtener sugerencias:', e);
        setAutocompleteOptions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300);
  };

  const fetchArticulos = async (page = 1) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: pagination.pageSize,
        aem_empresa: 1,
        ordenar_por: 'relevancia'
      });

      if (sucursalId) {
        queryParams.append('asu_sucursal', sucursalId);
      }

      if (busquedaTexto) {
        queryParams.append('busqueda_texto', busquedaTexto);
      }

      if (categoriaFiltro) {
        queryParams.append('aem_categoria_id', categoriaFiltro);
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal/sucursal/?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });

      const data = await res.json();
      setArticulos(data.data ?? []);
      setPagination({
        current: data.pagination?.currentPage ?? 1,
        total: data.pagination?.total ?? 0,
        pageSize: data.pagination?.limit ?? pagination.pageSize
      });
    } catch (error) {
      console.error('Error al obtener artículos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (page) => {
    fetchArticulos(page);
  };

  const handleSwitchChange = async (checked, record) => {
    try {
      const existeRegistro = record.datos_sucursal?.asu_id;
      
      if (existeRegistro) {
        // Si existe el registro, actualizar solo el estado habilitado
        const res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal/${existeRegistro}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ asu_habilitado: checked }),
        });

        if (!res.ok) throw new Error('Error al actualizar estado');
        
        message.success(checked ? 'Artículo habilitado' : 'Artículo deshabilitado');
        fetchArticulos(pagination.current);
      } else {
        // Si no existe, crear el registro con el estado habilitado
        const sucursalId = user.usu_sucursal;
        const res = await fetch(`${process.env.REACT_APP_API_URL}/articulo-sucursal`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            asu_articuloem: record.articulo_empresa.aem_id,
            asu_sucursal: sucursalId,
            asu_habilitado: checked,
            asu_precioventa: 0,
            asu_precio_siniva: 0,
          }),
        });

        if (!res.ok) throw new Error('Error al crear registro');
        
        message.success(checked ? 'Artículo habilitado' : 'Artículo deshabilitado');
        fetchArticulos(pagination.current);
      }
    } catch (error) {
      console.error(error);
      message.error('Error al actualizar estado');
      // Recargar para revertir el cambio visual
      fetchArticulos(pagination.current);
    }
  };

  const handleModificar = (record) => {
    setArticuloParaFormulario(record);
    setFormularioVisible(true);
  };

  const columns = [
    {
      title: 'Imagen',
      dataIndex: ['articulo_empresa', 'aem_imagen'],
      key: 'aem_imagen',
      width: 74,
      render: (imagen, record) =>
        imagen ? (
          <img
            src={imagen}
            alt={record?.articulo_empresa?.aem_nombre || 'artículo'}
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6 }}
          />
        ) : null,
    },
    {
      title: 'Nombre',
      dataIndex: ['articulo_empresa', 'aem_nombre'],
      key: 'aem_nombre',
    },
    {
      title: 'SKU',
      dataIndex: ['articulo_empresa', 'aem_sku'],
      key: 'aem_sku',
    },
    {
      title: 'Marca',
      dataIndex: ['articulo_empresa', 'aem_marca'],
      key: 'aem_marca',
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Tooltip title="Ver detalle">
          <Button
            type="text"
            icon={<EyeOutlined style={{ fontSize: 22 }} />}
            onClick={() => {
              setArticuloSeleccionado(record.articulo_empresa.aem_id);
              setVisible(true);
            }}
            style={{ 
              color: '#1890ff',
              border: 'none',
              boxShadow: 'none',
              width: 36,
              height: 36,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Tooltip>
      )
    },
    {
      title: 'Habilitado',
      key: 'asu_habilitado',
      render: (_, record) => (
        <Switch
          checked={record.datos_sucursal?.asu_habilitado || false}
          onChange={(checked) => handleSwitchChange(checked, record)}
        />
      )
    },
    {
      title: 'Modificar',
      key: 'modificar',
      render: (_, record) => (
        <Tooltip title="Editar artículo">
          <Button
            type="text"
            icon={<EditOutlined style={{ fontSize: 22 }} />}
            onClick={() => handleModificar(record)}
            style={{ 
              color: '#52c41a',
              border: 'none',
              boxShadow: 'none',
              width: 36,
              height: 36,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <div style={{ padding: '1rem' }}>
      {/* Barra de filtros superior */}
      <div
        style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          flexWrap: 'wrap',
          gap: '1rem',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        }}
      >
        <AutoComplete
          style={{ maxWidth: 500, width: '100%', flex: '0 1 auto' }}
          options={autocompleteOptions}
          onSearch={fetchAutocompleteSuggestions}
          onChange={(value) => {
            setSearchValue(value);
            if (value && value.trim().length >= 2) {
              fetchAutocompleteSuggestions(value);
            } else {
              setAutocompleteOptions([]);
            }
          }}
          onSelect={(value, option) => {
            const searchTerm = option?.item?.nombre || value;
            setSearchValue(searchTerm);
            setAutocompleteOptions([]);
            setPagination((p) => ({ ...p, current: 1 }));
            setBusquedaTexto(searchTerm.trim());
          }}
          value={searchValue}
          notFoundContent={autocompleteLoading ? <Spin size="small" /> : null}
          filterOption={false}
          dropdownMatchSelectWidth
        >
          <Input.Search
            placeholder="¿Qué estás buscando? (nombre, marca, código de barras...)"
            allowClear
            enterButton={
              <Button
                type="primary"
                icon={isBarcode(searchValue) ? <BarcodeOutlined /> : <SearchOutlined />}
                style={{ backgroundColor: '#01634C', borderColor: '#01634C', color: '#fff' }}
              />
            }
            onSearch={(value) => {
              setPagination((p) => ({ ...p, current: 1 }));
              const v = value && value.trim() ? value.trim() : '';
              setBusquedaTexto(v);
              setSearchValue(value || '');
            }}
            onChange={(e) => {
              const value = e.target.value;
              setSearchValue(value);
              if (!value || value.trim() === '') {
                setBusquedaTexto('');
                setAutocompleteOptions([]);
              } else if (value.trim().length >= 2) {
                fetchAutocompleteSuggestions(value);
              }
            }}
          />
        </AutoComplete>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          position: 'absolute',
          right: '1rem'
        }}>
          <CCategoriaInicio
            onCategoriaSeleccionada={(catId) => {
              setPagination(p => ({ ...p, current: 1 }));
              setCategoriaFiltro(catId);
            }}
          />
        </div>
      </div>

      <Row justify="space-between" align="middle" style={{ marginBottom: '1rem' }}>
        <Col><h2>Artículos de Empresa</h2></Col>
      </Row>

      <Table
        columns={columns}
        dataSource={articulos}
        rowKey={(record) => record.articulo_empresa.aem_id}
        loading={loading}
        pagination={false}
      />

      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={handleTableChange}
        style={{ marginTop: '1rem', textAlign: 'right' }}
      />

      <Modal
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={'90%'}
        style={{ top: 20 }}
        destroyOnClose
      >
        {articuloSeleccionado && (
          <CDetalleArticuloEmpresaSucursal articuloId={articuloSeleccionado} />
        )}
      </Modal>

      <CFormularioArticuloSucursal
        articulo={articuloParaFormulario}
        open={formularioVisible}
        onClose={() => setFormularioVisible(false)}
        onSuccess={() => fetchArticulos(pagination.current)}
      />
    </div>
  );
};

export default CListaArticulosEmpresaSucursal;